import { AppConstant, KeyConstant, PathConstant, SystemConstant } from "const";
import { EventSubType } from "pubsub/PubSub.const";
import store, { ConversationActions, SystemActions } from "redux-store";
import { CommonBranchInfoService, ElectronService } from "services";
import { convertString2JSON, StorageUtil, toCamel } from "utils";
import { isLoginBranch } from "utils/auth.utils";
import { changeBranchServer } from "utils/branch.utils";

const { Semaphore } = require("async-mutex");

/**
 * Global semaphore store theo serverId
 * @type {Record<string, import("async-mutex").Semaphore>}
 */
const semaphoreStore = {};
// Connect to DB

/**
 * Lấy hoặc tạo Semaphore cho serverId
 * @param {string} prefixKey
 * @param {number} concurrency Số lượng cho phép chạy song song (default = 1)
 * @returns {import("async-mutex").Semaphore}
 */
export const getSemaphore = (prefixKey, concurrency = 1) => {
  if (!semaphoreStore[prefixKey]) {
    semaphoreStore[prefixKey] = new Semaphore(concurrency);
  }
  return semaphoreStore[prefixKey];
};

export const getInteractor = prefixKey => {
  const interactor = window.interactor.trios.getInstance(prefixKey || StorageUtil.getCurrentPrefixKey());
  return {
    // Independent - the data is saved in branch's database (format name: <accountId_branchId>.db)
    LocalAccountService: interactor.account,
    LocalAccountKeyService: interactor.accountKey,
    LocalGroupService: interactor.group,
    LocalGroupSettingService: interactor.groupSetting,
    LocalSettingService: interactor.setting,
    LocalBranchAccountService: interactor.branchAccount,
    LocalBranchService: interactor.branch,
    LocalContactService: interactor.contact,
    LocalMessageService: interactor.message,
    LocalDeviceService: interactor.device,
    LocalAccountGroupService: interactor.accountGroup,
    LocalInitDataService: interactor.initData,

    LocalNotificationService: interactor.notification,
    LocalSenderKeySharedService: interactor.senderKeyShared,
    LocalEmojiService: interactor.emoji,
    LocalCallHistoryService: interactor.callHistory,
    LocalMsgErrorResendService: interactor.messageErrorResend,
    LocalMsgErrorSendNullService: interactor.messageErrorSendNull,
    LocalThreadService: interactor.thread,
    LocalRestoreService: interactor.restore,
    LocalApiCallService: interactor.apiCall,
    LocalEventService: interactor.events,

    LocalCryptoService: interactor.crypto,
    LocalReceivedMemberService: interactor.receivedMember,
    LocalSeenMemberService: interactor.seenMember,
    LocalFileService: interactor.file,
    LocalFileGroupService: interactor.fileGroup,
    LocalGroupMessageService: interactor.groupMessage,

    LocalAccountSettingService: interactor.accountSetting,
    LocalThreadSettingService: interactor.threadSetting,
    LocalDBVersionService: interactor.dbVersion,

    LocalMessageEncryptService: interactor.messageEncrypt,
  };
};

export const getCommonInteractor = () => window.interactor.trios.getCommonInteractor();

// Common - logic is not related prefixKey (common function or query data in trios.db)
export const LocalDbManagement = getCommonInteractor().dbManagement;
export const LocalKeyActionService = getCommonInteractor().keyAction;
export const LocalDeviceSettingService = getCommonInteractor().deviceSetting;
export const LocalApiCallService = getCommonInteractor().apiCall;

export const LocalAppNotificationService = {
  setBadgeCount: count => {
    window.electronEvent.emitEvent(AppConstant.TRIOS_EMIT_EVENT_KEY.setBadgeCount, count || 0);
  },

  showNotification: (title, noticeData, newNotification) => {
    const { content, groupId, threadId, messageSourceId, prefixKey } = noticeData;

    store.dispatch(
      SystemActions.systemSet({
        newNotification: { groupId, messageSourceId, prefixKey, ...newNotification },
      }),
    );

    const branchInfo = StorageUtil.getItem(KeyConstant.KEY_BRANCH_INFO, prefixKey) || {};
    const branchLogo =
      branchInfo.domain && branchInfo.id
        ? CommonBranchInfoService.getBranchAvatarUrl(branchInfo.domain, branchInfo.id)
        : "";

    if (!window.Notification) {
      console.warn("Browser does not support notifications."); // eslint-disable-line no-console
    } else {
      let options = {};
      if (messageSourceId) {
        options = {
          tag: messageSourceId,
          renotify: true,
        };
      }
      createNotification(
        title,
        {
          icon: branchLogo,
          body: content,
          ...options,
        },
        async () => {
          ElectronService.showApplication();

          const notScreenLockPage = false === Boolean(window.location.href.includes(PathConstant.SCREEN_LOCK));
          const notChooseBranchPage = false === Boolean(window.location.href.includes(PathConstant.CHOOSE_BRANCH_PAGE));
          if (isLoginBranch(branchInfo) && notScreenLockPage && notChooseBranchPage) {
            // Switch to branch
            changeBranchServer(branchInfo);

            if (newNotification && newNotification.type === SystemConstant.NotifyType.BRANCH_REQUEST) {
              // Open request form dialog
              store.dispatch(
                SystemActions.systemSet({
                  requestNotice: toCamel(convertString2JSON(newNotification.options, {})),
                }),
              );
            } else if (newNotification && newNotification.type === SystemConstant.NotifyType.OWNER_NOTIFICATION) {
              const group = await getInteractor(prefixKey).LocalGroupService.get(newNotification.groupId);
              if (newNotification.subType === EventSubType.ADD_ACCOUNT && Boolean(group && group.id)) {
                store.dispatch(
                  ConversationActions.setSelectGroupId({
                    selectedGroupId: newNotification.groupId,
                  }),
                );
              }
            } else {
              // Go to message by groupId/ messageId
              store.dispatch(
                ConversationActions.setSelectGroupId({
                  selectedGroupId: groupId,
                  threadingId: threadId,
                  scrollToChildId: messageSourceId,
                }),
              );
            }
          }
        },
      );
    }
  },
};

const createNotification = async (title, notificationOptions = {}, onClick) => {
  let notice;
  // Check if permission is already granted
  if (Notification.permission === "granted") {
    // Show notification here
    notice = new Notification(title, notificationOptions);
  } else {
    try {
      // Request permission from user
      const permission = await Notification.requestPermission();
      if (permission === "granted") {
        // Show notification here
        notice = new Notification(title, notificationOptions);
      } else {
        console.error("User blocked notifications.");
      }
    } catch (error) {
      console.error(error);
    }
  }

  if (onClick) notice.onclick = onClick;

  return notice;
};
